/**
 * ChatDemoTile.tsx — Animated AI chat demo tile for the MyLojic landing page
 *
 * v1.1 - PORTAL-001: Fixed minHeight to prevent layout shift during animation - 2026-03-26
 * v1.0 - 2026-03-23
 *
 * Plays a one-shot animation when scrolled 30% into viewport (IntersectionObserver).
 * Animation never loops. Observer disconnects after first trigger.
 * All demo content comes from src/data/chatDemos.ts — zero text hardcoded here.
 * No third-party animation libraries — CSS transitions and setTimeout only.
 *
 * Designed for dark backgrounds (Hero section: secondary.main = always dark navy).
 * To add a new demo scenario: add an entry to chatDemos.ts only.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { chatDemos, ChatDemo } from '../data/chatDemos';

const TEAL = '#00C9A7';

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Parse a metric string into countable parts for count-up animation.
 * Handles patterns like "31%", "22%", "40%", "3%", "$34K".
 * Returns null if metric cannot be parsed (fade-in used instead).
 */
function parseMetric(metric: string): { prefix: string; value: number; suffix: string } | null {
  const match = metric.match(/^(\$?)(\d+(?:\.\d+)?)([%KMBk]?)$/);
  if (!match) return null;
  return { prefix: match[1], value: parseFloat(match[2]), suffix: match[3] };
}

// ── Component ──────────────────────────────────────────────────────────────────

const ChatDemoTile: React.FC = () => {
  // Fixed selection for lifetime of page load — useMemo with [] deps
  const demo: ChatDemo = useMemo(
    () => chatDemos[Math.floor(Math.random() * chatDemos.length)],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const timerRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Animation state flags — set sequentially via setTimeout
  const [visible, setVisible] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [typedCount, setTypedCount] = useState(0);
  const [typingDone, setTypingDone] = useState(false);
  const [showDots, setShowDots] = useState(false);
  const [showMetric, setShowMetric] = useState(false);
  const [metricDisplay, setMetricDisplay] = useState('');
  const [showMetricLabel, setShowMetricLabel] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);

  const addTimer = (fn: () => void, ms: number) => {
    const t = setTimeout(fn, ms);
    timerRefs.current.push(t);
    return t;
  };

  useEffect(() => {
    const parsed = parseMetric(demo.metric);
    const CHAR_DELAY = 35; // ms per character

    function startAnimation() {
      // Step 1 — 0ms: fade tile in (opacity 0→1, 400ms via CSS transition)
      setVisible(true);

      // Step 2 — 500ms: input field appears
      addTimer(() => setShowInput(true), 500);

      // Step 3 — 900ms: "Ask MyLojic ›" label appears with blinking cursor
      addTimer(() => setShowLabel(true), 900);

      // Step 4 — 1100ms: question types one character at a time at 35ms/char
      addTimer(() => {
        let i = 0;
        const typeChar = () => {
          i++;
          setTypedCount(i);
          if (i < demo.question.length) {
            const t = setTimeout(typeChar, CHAR_DELAY);
            timerRefs.current.push(t);
          } else {
            // Step 5 — typing complete: cursor stops, 600ms pause
            setTypingDone(true);
            addTimer(() => {
              // Step 6 — thinking dots appear (pulsing with staggered CSS delay)
              setShowDots(true);

              // Step 7 — 1200ms: dots fade out
              addTimer(() => {
                setShowDots(false);

                // Step 8 — metric appears with count-up (numeric) or fade-in
                addTimer(() => {
                  if (parsed) {
                    setMetricDisplay(`${parsed.prefix}0${parsed.suffix}`);
                  } else {
                    setMetricDisplay(demo.metric);
                  }
                  setShowMetric(true);

                  if (parsed) {
                    // Count-up over 600ms in 30 steps
                    const steps = 30;
                    const stepMs = 600 / steps;
                    for (let s = 1; s <= steps; s++) {
                      addTimer(() => {
                        const v = Math.round((parsed.value * s) / steps);
                        setMetricDisplay(`${parsed.prefix}${v}${parsed.suffix}`);
                      }, s * stepMs);
                    }
                  }

                  // Step 9 — 200ms after metric: metric label fades in
                  addTimer(() => setShowMetricLabel(true), 200);

                  // Step 10 — 600ms after label: recommendation + divider fade in
                  addTimer(() => setShowRecommendation(true), 600);

                  // Step 11 — animation complete, all elements remain visible
                }, 150);
              }, 1200);
            }, 600);
          }
        };
        typeChar();
      }, 1100);
    }

    // Scroll-triggered — fires exactly once, then observer disconnects
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          startAnimation();
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    const timers = timerRefs.current;
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [demo]); // eslint-disable-line react-hooks/exhaustive-deps

  const showCursor = showLabel && !typingDone;
  const displayedText = demo.question.slice(0, typedCount);

  return (
    <Box
      ref={containerRef}
      sx={{
        // Tile container — frosted glass on dark navy background
        opacity: visible ? 1 : 0,
        transition: 'opacity 400ms ease',
        background: 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
        padding: { xs: '28px 24px', md: '40px 48px' },
        width: '100%',
        maxWidth: { xs: '100%', sm: '480px' },
        mx: 'auto',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // v1.1 - PORTAL-001: Fixed minHeight to accommodate tallest fully-revealed demo
        // (input area ~100px + metric ~80px + label ~68px + recommendation ~60px + padding 80px = ~388px).
        // Container starts at this height before animation so the hero section never reflows.
        // Using minHeight (not height) so content can gracefully overflow on very narrow screens.
        // Tile starts opacity:0 so the reserved space is invisible until animation fires.
        minHeight: { xs: '420px', md: '420px' },
        overflow: 'hidden',
      }}
    >
      {/* ── Input area ── */}
      <Box
        sx={{
          width: '100%',
          mb: 3,
          opacity: showInput ? 1 : 0,
          transition: 'opacity 300ms ease',
        }}
      >
        {/* "Ask MyLojic ›" label row with teal M mark */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            opacity: showLabel ? 1 : 0,
            transition: 'opacity 300ms ease',
          }}
        >
          {/* Teal circle with white M (fallback mark) */}
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: TEAL,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 0.75,
              flexShrink: 0,
            }}
          >
            <Typography
              component="span"
              sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 800, lineHeight: 1 }}
            >
              M
            </Typography>
          </Box>
          <Typography sx={{ color: TEAL, fontSize: '0.78rem', fontWeight: 600 }}>
            Ask MyLojic ›
          </Typography>
        </Box>

        {/* Input field — styled box, readOnly by design (no <input> to trigger mobile keyboard) */}
        <Box
          sx={{
            bgcolor: 'rgba(0,0,0,0.35)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            minHeight: 44,
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              color: typedCount > 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
              fontSize: '0.9rem',
              lineHeight: 1.4,
              flex: 1,
              fontStyle: typedCount > 0 ? 'normal' : 'italic',
            }}
          >
            {typedCount > 0 ? displayedText : 'Ask anything about your business...'}
          </Typography>

          {/* Blinking cursor — visible during typing only */}
          {showCursor && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '2px',
                height: '1em',
                bgcolor: TEAL,
                ml: 0.25,
                flexShrink: 0,
                animation: 'mlBlink 1s step-end infinite',
                '@keyframes mlBlink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 },
                },
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Thinking dots — staggered pulse, teal ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          justifyContent: 'center',
          mb: showDots ? 2 : 0,
          height: showDots ? 16 : 0,
          opacity: showDots ? 1 : 0,
          transition: 'opacity 300ms ease, height 300ms ease, margin-bottom 300ms ease',
          overflow: 'hidden',
        }}
      >
        {[0, 150, 300].map((delay, i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: TEAL,
              animation: showDots
                ? `mlDotPulse 0.9s ${delay}ms ease-in-out infinite`
                : 'none',
              '@keyframes mlDotPulse': {
                '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                '50%': { opacity: 1, transform: 'scale(1.2)' },
              },
            }}
          />
        ))}
      </Box>

      {/* ── Answer block — metric + label + recommendation ── */}
      <Box
        sx={{
          opacity: showMetric ? 1 : 0,
          transition: 'opacity 400ms ease',
          width: '100%',
        }}
      >
        {/* Metric number — large, bold, teal, with count-up */}
        <Typography
          sx={{
            fontSize: { xs: '3rem', md: '4.5rem' },
            fontWeight: 700,
            color: TEAL,
            lineHeight: 1,
            mb: 1,
          }}
        >
          {metricDisplay}
        </Typography>

        {/* Metric label */}
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '1rem',
            mb: 2.5,
            maxWidth: '340px',
            mx: 'auto',
            opacity: showMetricLabel ? 1 : 0,
            transition: 'opacity 400ms ease',
          }}
        >
          {demo.metricLabel}
        </Typography>

        {/* Thin teal divider + recommendation in italic */}
        <Box
          sx={{
            opacity: showRecommendation ? 1 : 0,
            transition: 'opacity 400ms ease',
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 1,
              bgcolor: TEAL,
              mx: 'auto',
              mb: 2,
            }}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.9rem',
              fontStyle: 'italic',
              maxWidth: '340px',
              mx: 'auto',
            }}
          >
            {demo.recommendation}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatDemoTile;
